/**
 * Packaging OS — FormField primitives (Phase 1P-B).
 *
 * Labeled input / textarea / select wrappers with required-marker, hint,
 * and error slots. All token-only. No third-party form library.
 */

import type {
  ChangeEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";

interface BaseFieldProps {
  label: string;
  required?: boolean;
  requiredLabel?: string;
  hint?: string;
  error?: string | null;
  full?: boolean;
}

interface TextFieldProps extends BaseFieldProps {
  value: string;
  onChange: (next: string) => void;
  type?: "text" | "email" | "url";
  placeholder?: string;
  maxLength?: number;
}

export function TextField({
  label,
  required,
  requiredLabel,
  hint,
  error,
  full,
  value,
  onChange,
  type = "text",
  placeholder,
  maxLength,
}: TextFieldProps): JSX.Element {
  const id = useId();
  const cls = `po-field${full ? " po-field--full" : ""}${
    error ? " po-field--invalid" : ""
  }`;
  return (
    <div className={cls}>
      <label className="po-field__label" htmlFor={id}>
        <span>{label}</span>
        {required && requiredLabel && (
          <span className="po-field__required">{requiredLabel}</span>
        )}
      </label>
      <input
        id={id}
        type={type}
        className="po-input"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
      {hint && (
        <p id={`${id}-hint`} className="po-field__hint">
          {hint}
        </p>
      )}
      {error && <p className="po-field__error">{error}</p>}
    </div>
  );
}

interface TextAreaFieldProps extends BaseFieldProps {
  value: string;
  onChange: (next: string) => void;
  rows?: number;
  maxLength?: number;
  textareaProps?: TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export function TextAreaField({
  label,
  required,
  requiredLabel,
  hint,
  error,
  full,
  value,
  onChange,
  rows = 4,
  maxLength,
  textareaProps,
}: TextAreaFieldProps): JSX.Element {
  const id = useId();
  const cls = `po-field${full ? " po-field--full" : ""}${
    error ? " po-field--invalid" : ""
  }`;
  return (
    <div className={cls}>
      <label className="po-field__label" htmlFor={id}>
        <span>{label}</span>
        {required && requiredLabel && (
          <span className="po-field__required">{requiredLabel}</span>
        )}
      </label>
      <textarea
        id={id}
        className="po-textarea"
        rows={rows}
        maxLength={maxLength}
        value={value}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        aria-invalid={error ? true : undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
        {...textareaProps}
      />
      {hint && (
        <p id={`${id}-hint`} className="po-field__hint">
          {hint}
        </p>
      )}
      {error && <p className="po-field__error">{error}</p>}
    </div>
  );
}

interface SelectFieldProps extends BaseFieldProps {
  value: string;
  onChange: (next: string) => void;
  options: Array<{ value: string; label: string }>;
  selectProps?: SelectHTMLAttributes<HTMLSelectElement>;
  /** Optional empty-value label (e.g. "— not selected —"). */
  emptyLabel?: string;
}

export function SelectField({
  label,
  required,
  requiredLabel,
  hint,
  error,
  full,
  value,
  onChange,
  options,
  emptyLabel,
  selectProps,
}: SelectFieldProps): JSX.Element {
  const id = useId();
  const cls = `po-field${full ? " po-field--full" : ""}${
    error ? " po-field--invalid" : ""
  }`;
  return (
    <div className={cls}>
      <label className="po-field__label" htmlFor={id}>
        <span>{label}</span>
        {required && requiredLabel && (
          <span className="po-field__required">{requiredLabel}</span>
        )}
      </label>
      <select
        id={id}
        className="po-select"
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          onChange(e.target.value)
        }
        aria-invalid={error ? true : undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
        {...selectProps}
      >
        {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && (
        <p id={`${id}-hint`} className="po-field__hint">
          {hint}
        </p>
      )}
      {error && <p className="po-field__error">{error}</p>}
    </div>
  );
}

export function FieldGrid({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  return <div className="po-form-grid">{children}</div>;
}
